import { useState, useEffect, useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Calendar, Clock, ChevronRight, Search, BookOpen, Sparkles, TrendingUp, Lightbulb } from 'lucide-react'
import SEO from '../components/SEO'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  image: string
  category: string
  author: string
  date: string
  readTime: number
}

const categories = ['Tutti', 'Risparmio', 'Guide', 'Internet', 'Bonus', 'Streaming', 'Energia', 'Novita']

const featuredTopics = [
  { icon: Lightbulb, label: 'Risparmio', color: 'from-yellow-400 to-orange-500' },
  { icon: BookOpen, label: 'Guide', color: 'from-blue-400 to-cyan-500' },
  { icon: TrendingUp, label: 'Novità', color: 'from-green-400 to-emerald-500' },
]

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState('Tutti')
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true)
      try {
        const response = await fetch('/api/posts')
        const data = await response.json()
        setPosts(data)
      } catch (error) {
        console.error('Error fetching posts:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchPosts()
  }, [])

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = activeCategory === 'Tutti' || post.category === activeCategory
    return matchesSearch && matchesCategory
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  return (
    <div className="overflow-hidden">
      <SEO
        title="Il Blog di Carletto - Guide e Consigli per Risparmiare"
        description="Scopri trucchi, guide pratiche e novità per risparmiare su luce, gas, internet e streaming. Carletto ti svela tutti i segreti per tagliare le bollette!"
        canonical="/il-blog-di-carletto"
        keywords="blog risparmio, guide luce gas, consigli bollette, risparmiare energia, offerte fibra, streaming economico"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/il-blog-di-carletto' },
        ]}
      />
      {/* Dynamic Hero Section */}
      <section ref={heroRef} className="relative min-h-[70vh] flex items-center pt-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-blue-50" />
          <div className="absolute inset-0 pattern-dots opacity-30" />
          
          {/* Animated Blobs */}
          <motion.div 
            className="absolute top-20 right-10 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-blue-400/20 rounded-full blur-3xl"
            animate={{ 
              scale: [1.2, 1, 1.2],
              y: [0, -30, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute top-1/2 left-1/3 w-[300px] h-[300px] bg-green-400/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 backdrop-blur-sm border border-primary/20 text-primary-700 font-semibold rounded-full text-sm mb-6"
              >
                <Sparkles className="w-4 h-4" />
                Consigli e guide per risparmiare
              </motion.div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-dark-800 mb-6">
                Il Blog di{' '}
                <span className="text-primary relative inline-block">
                  Carletto
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 10C50 4 150 4 198 10" stroke="#F8B120" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                </span>
              </h1>
              
              <p className="text-xl text-dark-500 mb-8 max-w-lg">
                Scopri trucchi, guide pratiche e novità per <span className="font-semibold text-dark-700">risparmiare</span> su 
                luce, gas, internet e molto altro. Carletto ti svela tutti i segreti!
              </p>

              {/* Featured Topics */}
              <div className="flex flex-wrap gap-3 mb-8">
                {featuredTopics.map((topic, index) => (
                  <motion.div
                    key={topic.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-dark-100 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setActiveCategory(topic.label === 'Novità' ? 'Tutti' : topic.label)}
                  >
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${topic.color} flex items-center justify-center`}>
                      <topic.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-dark-700">{topic.label}</span>
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-8"
              >
                <div>
                  <div className="text-2xl font-bold text-dark-800">{posts.length || '50+'}+</div>
                  <div className="text-sm text-dark-500">Articoli</div>
                </div>
                <div className="w-px h-10 bg-dark-200" />
                <div>
                  <div className="text-2xl font-bold text-dark-800">10k+</div>
                  <div className="text-sm text-dark-500">Lettori mensili</div>
                </div>
                <div className="w-px h-10 bg-dark-200" />
                <div>
                  <div className="text-2xl font-bold text-dark-800">€2.5M</div>
                  <div className="text-sm text-dark-500">Risparmiati</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Right - Carletto with floating elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative h-[500px] flex items-center justify-center">
                {/* Animated Rings */}
                <motion.div 
                  className="absolute w-[350px] h-[350px] border-2 border-primary/20 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                />
                <motion.div 
                  className="absolute w-[450px] h-[450px] border border-primary/10 rounded-full"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                />
                
                {/* Glow */}
                <div className="absolute w-72 h-72 bg-primary/20 blur-3xl rounded-full" />
                
                {/* Carletto */}
                <motion.img
                  src="/carletto/ChatGPT Image 8 apr 2025, 09_55_30.png"
                  alt="Carletto blogger"
                  className="relative z-10 w-80 drop-shadow-2xl"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Floating Cards */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute top-10 left-0"
                >
                  <motion.div 
                    className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, -8, 0], rotate: [0, 2, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800 text-sm">Guide pratiche</p>
                      <p className="text-xs text-dark-500">Sempre aggiornate</p>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute top-32 right-0"
                >
                  <motion.div 
                    className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, 10, 0], rotate: [0, -2, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800 text-sm">50+ articoli</p>
                      <p className="text-xs text-dark-500">Da leggere</p>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute bottom-16 left-8"
                >
                  <motion.div 
                    className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, -6, 0], rotate: [0, 1, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Lightbulb className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800 text-sm">Consigli gratis</p>
                      <p className="text-xs text-dark-500">Da Carletto</p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <a href="#articles" className="flex flex-col items-center text-dark-400 hover:text-primary transition-colors">
            <span className="text-sm mb-2">Esplora gli articoli</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronRight className="w-6 h-6 rotate-90" />
            </motion.div>
          </a>
        </motion.div>
      </section>

      {/* Search and Articles Section */}
      <section id="articles" className="py-16 bg-dark-50">
        <div className="container-custom">
          {/* Search and Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            {/* Search Bar */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                <input
                  type="text"
                  placeholder="Cerca articoli..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white border border-dark-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
                />
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                    activeCategory === category
                      ? 'bg-primary text-dark-900 shadow-lg shadow-primary/30'
                      : 'bg-white text-dark-600 hover:bg-dark-100 border border-dark-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="bg-dark-100 h-48 rounded-xl mb-4" />
                <div className="bg-dark-100 h-4 w-20 rounded mb-3" />
                <div className="bg-dark-100 h-6 rounded mb-3" />
                <div className="bg-dark-100 h-4 rounded mb-2" />
                <div className="bg-dark-100 h-4 w-3/4 rounded" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <img
              src="/carletto/9.png"
              alt="Carletto"
              className="w-48 h-auto mx-auto mb-6"
            />
            <h3 className="text-2xl font-display font-bold mb-2">Torna presto!</h3>
            <p className="text-dark-500">
              Stiamo preparando tanti nuovi articoli per te. Scopri tutte le novità!
            </p>
          </motion.div>
        ) : filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.slug}`} className="card block group overflow-hidden">
                  {/* Image */}
                  <div className="relative h-48 bg-primary/10 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                    />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-dark-700">
                      {post.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-xl font-display font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-dark-500 mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm text-dark-400">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime} min
                        </span>
                      </div>
                    </div>

                    {/* Read More */}
                    <div className="mt-4 pt-4 border-t border-dark-100">
                      <span className="inline-flex items-center text-primary font-semibold group-hover:gap-2 transition-all">
                        Leggi di più
                        <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <img
              src="/carletto/9.png"
              alt="Nessun risultato"
              className="w-48 h-auto mx-auto mb-6"
            />
            <h3 className="text-2xl font-display font-bold mb-2">Nessun articolo trovato</h3>
            <p className="text-dark-500">
              Prova a modificare i filtri o la ricerca
            </p>
          </motion.div>
        )}
        </div>
      </section>
    </div>
  )
}
